import { InputNumber, Radio } from 'antd';
import FormField from 'components/FormField';
import { getStoreCount } from 'components/StoreScopes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AiModelState,
  defaultValueMap,
  getPrefixName,
  hideRadioClassName,
  Inline,
  Prefix,
  stores2storeScopes,
} from './share';
import { updateGift } from 'views/BootGuide/service';
import { StoreDescription } from './ShippingDays';
import useSmartField from 'hooks/useSmartField';
import { regionMonetaryUnitMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
// import ItemSelect from '@knowledge/knowledges/general/edit/selectors/ItemSelect';
import styled from '@emotion/styled';
import NormalMode from './NormalMode';
import { T, useTranslate } from '@tolgee/react';
import TransComponent from 'components/TransComponent';

type FV = BootGuide.AiModel.FormValues.Gift;

type Props = { prefix?: Prefix; singleMode?: boolean };

export function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      NO_GIFT: t('本店不赠送赠品'),
      ALL_GIFT: t('本店购买任意商品都赠送赠品'),
      GIFT_BY_NUM: t('本店满指定件数赠送赠品'),
      GIFT_BY_AMOUNT: t('本店满指定金额赠送赠品'),
      // GIFT_BY_SPU: t('买指定商品送'),
    } as const;
  }, [t]);
}

export const useDefineLabel = () => {
  const { t } = useTranslate();
  return useCallback(
    (value: FV) => {
      if (value.value === 'NO_GIFT') {
        return t('本店不赠送赠品');
      } else if (value.value === 'ALL_GIFT') {
        return t('本店购买任意商品都赠送赠品');
      } else if (value.value === 'GIFT_BY_NUM') {
        return t('本店满{{X}}件数赠送赠品', { X: value.number });
      } else if (value.value === 'GIFT_BY_AMOUNT') {
        return t('本店满{{X}}赠送赠品', {
          X: value.currencyAmountList.map(({ price, currency }) => `${currency}${price}`).join('/'),
        });
      }
      return null;
    },
    [t],
  );
};

function Gift({ prefix, singleMode }: Props) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));

  const getHideStyle = (type: FV['value']) =>
    singleMode && value !== type ? { display: 'none' } : undefined;

  return (
    <div>
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2 mt-[6px]" style={getHideStyle(key)}>
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={value === key}
              onChange={({ target: { checked } }) => checked && onChange(key)}
            >
              {label[key]}
            </Radio>
            <FieldContainer>
              {key === 'GIFT_BY_NUM' && <GiftByNum prefix={prefix} singleMode={singleMode} />}
              {key === 'GIFT_BY_AMOUNT' && <GiftByAmount prefix={prefix} singleMode={singleMode} />}
              {/*{key === 'GIFT_BY_SPU' && <GiftBySpu prefix={prefix} singleMode={singleMode}/>}*/}
            </FieldContainer>
          </div>
        );
      })}
    </div>
  );
}

export function GiftByNum({ prefix }: Props) {
  const { t } = useTranslate();

  const {
    field: { value: giftType },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));

  if (giftType !== 'GIFT_BY_NUM' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <div>
      <FormField<FV['number']>
        name={getPrefixName('number', prefix)}
        required
        label=" "
        colon={false}
        validate={(value) => (value ? undefined : t('trade.请输入'))}
      >
        {({ field }) => {
          return (
            <Inline>
              <T
                keyName="满x件送"
                params={{
                  x: (
                    <TransComponent
                      content={<InputNumber min={1} max={999} precision={0} {...(field as any)} />}
                    />
                  ),
                }}
              />
            </Inline>
          );
        }}
      </FormField>
    </div>
  );
}

export function GiftByAmount({ prefix, singleMode }: Props) {
  const { t } = useTranslate();

  const {
    field: { value: giftType },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value = [], onChange },
  } = useSmartField<FV['currencyAmountList']>(getPrefixName('currencyAmountList', prefix));

  const regions = useMemo(() => {
    return storeScopes.reduce<General.UpperCaseRegion[]>((res, { regionStoreScopes }) => {
      return regionStoreScopes.reduce((res, { region }) => {
        if (!res.includes(region) && region !== 'all') {
          res.push(region);
        }
        return res;
      }, res);
    }, []);
  }, [storeScopes]);

  const refsCurrent = {
    value,
    onChange,
    singleMode,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const { value, onChange, singleMode } = refs.current;
    if (
      !singleMode &&
      (value.some(({ region }) => !regions.includes(region)) ||
        regions.some((region) => !value.some((_) => _.region === region)))
    ) {
      const nextValue = regions.map((region) => {
        const target = value.find((_) => _.region === region);
        if (target) {
          return target;
        }
        return {
          region,
          price: undefined as any,
          currency: regionMonetaryUnitMap[toLowerCase(region)],
        };
      });
      onChange(nextValue);
    }
  }, [regions]);

  if (giftType !== 'GIFT_BY_AMOUNT' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <div>
      {value.map(({ region, currency }, index) => {
        return (
          <FormField<FV['currencyAmountList'][0]>
            name={getPrefixName(`currencyAmountList.${index}`, prefix)}
            required
            label=" "
            colon={false}
            validate={(value) => (value.price ? undefined : t('请输入金额'))}
          >
            {({ field: { value, onChange, onBlur } }) => {
              return (
                <InputNumber
                  min={1}
                  precision={2}
                  value={value.price}
                  onChange={(price) => onChange({ ...value, price } as any)}
                  onBlur={onBlur}
                  addonBefore={region}
                  addonAfter={currency}
                />
              );
            }}
          </FormField>
        );
      })}
    </div>
  );
}

export function GiftBootGuide(props: AiModelState<FV>) {
  return (
    <NormalMode
      state={props}
      tips={props.t('您的店铺是否有赠品呢?以下是一些常见的选项供您选择和调整')}
      getFormContent={(prefix) => <Gift prefix={prefix} />}
      getFirstInitialValues={useCallback(({ stores }) => {
        return {
          values: [
            {
              ...defaultValueMap.AI_MODEL_GIFT,
              storeScopes: stores2storeScopes(stores),
            },
          ],
        };
      }, [])}
      updateApi={updateGift}
      getRemainTips={(remainLength) =>
        props.t('您有x家店铺还未配置，请选择店铺是否有赠品', { x: remainLength })
      }
      descriptionRender={(value, storeMap) => (
        <StoreDescription storeScopes={value} storeMap={storeMap} />
      )}
    />
  );
}

const FieldContainer = styled.div`
  > div {
    padding: 4px 12px;
    background: #f7f9fa;
    label::after {
      display: none;
    }
  }
`;

export default Gift;
